import { t as uiText } from '../i18n';
// src/components/UserMenu.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../lib/firebase';
import { useLogout } from '../features/auth/logout';
import { useUserImage } from '../hooks/useUserImage';
import { refreshStorageURL, isStorageURL } from '../features/workspace/imageUtils';
import { useFeatureInAnyWorkspace } from '../features/plans/useFeatureInAnyWorkspace';

interface UserMenuProps {
  onUserSettingsOpen?: () => void;
  onAvatarClick?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ onUserSettingsOpen, onAvatarClick }) => {
  const [user] = useAuthState(auth as any);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userAvatarError, setUserAvatarError] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const logout = useLogout();
  const { enabled: companyEnabled } = useFeatureInAnyWorkspace(user?.uid, 'company');

  const {
    imageUrl,
    imageData,
    isLoading: imageLoading,
    error: imageError,
    hasImage,
    reload: reloadImage,
  } = useUserImage();

  useEffect(() => {
    setUserAvatarError(false);
  }, [user?.uid, imageUrl, imageData]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!user) {
    return null;
  }

  const userInitial =
    user?.displayName?.trim().charAt(0) ||
    user?.email?.trim().charAt(0) ||
    'U';
  const shouldShowUserImage =
    !!(imageData || imageUrl) && !imageLoading && !userAvatarError;

  return (
    <div style={{ position: 'relative' }} ref={userMenuRef}>
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: 'bold',
          color: 'var(--workspace-primary, #cfae91)',
          cursor: 'pointer',
          overflow: 'hidden',
          border: '2px solid #fff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s',
        }}
        onClick={() => {
          setMenuOpen((prev) => !prev);
          onAvatarClick?.();
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title={uiText("User menu ({{v0}})", { v0: user?.displayName || user?.email || 'Unknown' })}
      >
        {shouldShowUserImage ? (
          <img
            src={imageData || imageUrl || ''}
            alt={user?.displayName || user?.email || 'User'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '50%',
              transition: 'opacity 0.15s ease',
            }}
            onError={async (e) => {
              const img = e.currentTarget;
              const originalSrc = img.src;

              if (isStorageURL(originalSrc)) {
                try {
                  const refreshedURL = await refreshStorageURL(originalSrc);
                  if (refreshedURL && refreshedURL !== originalSrc) {
                    img.src = refreshedURL;
                    return;
                  }
                } catch (error) {
                  console.warn('🖼️ [UserMenu] Storage URLリフレッシュ失敗:', error);
                }
              }

              setUserAvatarError(true);
            }}
            onLoad={() => {}}
          />
        ) : null}
        <div
          style={{
            width: '100%',
            height: '100%',
            display: shouldShowUserImage ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: 'var(--workspace-primary, #cfae91)',
          }}
        >
          {userInitial}
        </div>
      </div>

      {/* ユーザーメニュー */}
      {menuOpen && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '48px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            borderRadius: '4px',
            width: 'max-content',
            minWidth: '160px',
            maxWidth: '600px',
            zIndex: 10000,
            border: '1px solid #eee',
          }}
        >
          {/* ユーザー情報 */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #eee',
              backgroundColor: '#f8f9fa',
            }}
          >
            <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333' }}>
              {user.displayName || uiText("User")}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {user.email}
            </div>
          </div>

          {/* メニュー項目 */}
          <div
            style={{
              padding: '8px 0',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#333',
                transition: 'background-color 0.2s',
              }}
              onClick={() => {
                onUserSettingsOpen?.();
                setMenuOpen(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {uiText("User settings")}
            </div>
            {companyEnabled && (
              <div
                style={{
                  padding: '10px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#333',
                  transition: 'background-color 0.2s',
                }}
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    const adminUrl = new URL('/admin', window.location.origin).toString();
                    window.open(adminUrl, '_blank', 'noopener,noreferrer');
                  }
                  setMenuOpen(false);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {uiText("Organization account management")}
              </div>
            )}
            <div
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#333',
                transition: 'background-color 0.2s',
              }}
              onClick={() => {
                if (typeof window !== 'undefined') {
                  const billingUrl = new URL('/billing', window.location.origin).toString();
                  window.open(billingUrl, '_blank', 'noopener,noreferrer');
                }
                setMenuOpen(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {uiText("Payment management")}
            </div>
            <div
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#333',
                transition: 'background-color 0.2s',
              }}
              onClick={() => {
                reloadImage();
                setMenuOpen(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {uiText("Reload image")}
            </div>
            <div
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#dc3545',
                transition: 'background-color 0.2s',
              }}
              onClick={() => {
                logout();
                setMenuOpen(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fff5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {uiText("Log out")}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
